import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable, split } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMemo, type ReactNode } from "react";
import { buildAppSyncSubscriptionLink, isSubscriptionOperation } from "@athena/realtime";
import { getEnv } from "./env";

/**
 * Auth0 error codes indicating the cached session/refresh token can no longer
 * be silently renewed (e.g. it was minted under a stale client id, revoked, or
 * expired). These require a fresh interactive login, not a retry.
 */
const REAUTH_REQUIRED_ERRORS = new Set([
    "login_required",
    "invalid_grant",
    "missing_refresh_token",
    "consent_required",
]);

/**
 * `offsetLimitPagination`, minus its append-on-missing-arguments fallback.
 *
 * Apollo's helper splices `incoming` in at `args.offset`, but when the field is selected with no
 * arguments at all `args` is null and it appends the whole list instead — its own comment calls
 * that "probably a mistake" and recovers rather than throwing. Several documents here select a
 * paged field bare because they want the lot (`PushNotificationCourseOptions` asks for `courses`
 * with no arguments), and with the client's default `cache-and-network` policy every remount then
 * wrote another full copy into the list: a course picker that grows a duplicate set per visit.
 *
 * No arguments means offset 0, which is what an unpaged read of a paged field actually is.
 */
const offsetLimitPaging = (keyArgs: string[] | false = false) => ({
    keyArgs,
    merge(existing: unknown[] | undefined, incoming: unknown[], { args }: { args: Record<string, unknown> | null }) {
        const merged = existing ? existing.slice(0) : [];
        const offset = (args?.offset as number | undefined) ?? 0;
        for (let i = 0; i < incoming.length; i += 1) merged[offset + i] = incoming[i];
        return merged;
    },
});

/**
 * Where the one permitted re-authentication attempt is recorded.
 *
 * `sessionStorage` rather than a module variable or React state, because the attempt has to
 * outlive a full page navigation — the redirect leaves for Auth0 and comes back to a fresh
 * document, where anything held in memory has been thrown away. Per-tab, and gone when the tab is.
 */
const REAUTH_ATTEMPT_KEY = "athena.admin.reauthAttempted";

/**
 * Records an attempt, and says whether this caller is the one allowed to make it.
 *
 * The redirect below used to be unconditional, which is a loop whenever the thing sending the user
 * to log in is also what fails after they have: a post-login Action returning `access_denied` put
 * a pen tester through it for three days, and the only symptom was landing back on the login page.
 * One redirect fixes an expired token; a second one never fixes anything.
 */
const claimReauthAttempt = (): boolean => {
    try {
        if (sessionStorage.getItem(REAUTH_ATTEMPT_KEY)) return false;
        sessionStorage.setItem(REAUTH_ATTEMPT_KEY, "1");
        return true;
    } catch {
        // Storage blocked (private windows, hardened settings). One redirect is still the right
        // move for the common expired-token case, and without anywhere to record it this is the
        // old behavior — a loop is possible again, but only where it was already unavoidable.
        return true;
    }
};

/** A working token means whatever went wrong is over. */
const releaseReauthAttempt = (): void => {
    try {
        sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
    } catch {
        // Nothing was recorded, so there is nothing to clear.
    }
};

/**
 * The cache, and the paging policies that make the admin's tables work.
 *
 * Exported and built by a function rather than inlined in the provider so the policies can be
 * tested. They are the part of pagination that fails silently: a field missing an appending merge
 * has each `fetchMore` overwrite the list instead of growing it, and a field missing a filter in
 * its `keyArgs` lands a narrowed page on top of the unfiltered one — both of which render as
 * plausible-looking wrong data rather than as an error.
 */
export const createAdminCache = (): InMemoryCache =>
    new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    /*
                     * Paged, keyed on the filter.
                     *
                     * This was `{ merge: false }` — replace wholesale — which was right
                     * while the page made one unbounded request and wrong the moment it
                     * started asking for a second page: every `fetchMore` would have
                     * discarded the rows above it and the table would never grow.
                     *
                     * Keyed on `filter` so changing the search term starts a new list
                     * rather than appending strangers to the previous one at offset 0.
                     */
                    users: offsetLimitPaging([
                        "filter",
                    ]),
                    /*
                     * Paged reads append instead of replacing.
                     *
                     * `useAllRows` walks these lists a page at a time. Without a merge
                     * policy the cache overwrites the field on each `fetchMore`, so a
                     * 620-video library would settle on whichever page arrived last —
                     * a worse bug than the 50-row truncation being fixed, because the
                     * count looks plausible.
                     *
                     * Keyed by `organizationId` *and* `search` so narrowing starts a fresh
                     * list rather than merging two different questions' answers at the same
                     * offsets. Leaving `search` out meant a search's first page landed on
                     * top of the unfiltered list and the rows below it stayed visible.
                     */
                    videoAssets: offsetLimitPaging([
                        "organizationId",
                        "search",
                    ]),
                    courses: offsetLimitPaging([
                        "organizationId",
                    ]),
                    // No key args: the inbox is one list, scoped by who is asking rather
                    // than by any argument.
                    chats: offsetLimitPaging(),
                    /*
                     * Not paged at all: `organizations(topLevelOnly: Boolean)` takes no
                     * offset or limit (common/graphql/organization.graphql), so every
                     * response is the whole list and each one replaces the last.
                     *
                     * It was `offsetLimitPagination`, and the option queries select the
                     * field bare — so each remount appended another full copy and the
                     * course page's organization select listed every university once per
                     * visit, with the trigger printing the selected name once per duplicate.
                     *
                     * Keyed on the shape argument still: the tree view and the flat option
                     * list are different lists, and one replacing the other would drop rows
                     * the other needs.
                     */
                    organizations: {
                        keyArgs: [
                            "topLevelOnly",
                        ],
                        merge: false,
                    },
                    /*
                     * One organization's roster, paged.
                     *
                     * Every argument that narrows it is a key arg. `organizationId` most
                     * obviously — two organizations' rosters merging into one list would
                     * show a partner the wrong members — and `search` and `licensed` for
                     * the same reason they are on `videoAssets`: a filtered page landing
                     * at offset 0 of the unfiltered list leaves the rows beneath it on
                     * screen, so the table shows people who do not match.
                     */
                    organizationMembers: offsetLimitPaging([
                        "organizationId",
                        "search",
                        "licensed",
                    ]),
                    /*
                     * The invitation half of the same roster, paged the same way.
                     *
                     * Without a merge policy `fetchMore` overwrites rather than appends, so the
                     * list never grows past its first page — which is not a visible failure, it is
                     * a Next button that returns you to where you started. `status` is a key arg
                     * because ACTIVE and PENDING are two different lists behind one field name.
                     */
                    organizationRoster: offsetLimitPaging([
                        "organizationId",
                        "status",
                        "search",
                    ]),
                },
            },
        },
    });

/**
 * Apollo client built inside the Auth0 React tree so it can attach the
 * current user's access token via `getAccessTokenSilently`. Anonymous
 * requests proceed without an Authorization header; the backend treats
 * those as unauthenticated.
 *
 * `isAuthenticated` only reflects a cached session — it doesn't guarantee the
 * refresh token backing it is still valid. When Auth0 rejects a silent token
 * refresh, redirect to a fresh login instead of leaving every query to fail
 * silently into an empty UI.
 */
interface Props {
    children: ReactNode;
}

export function AuthorizedApolloProvider({ children }: Props) {
    const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();

    const client = useMemo(() => {
        const httpLink = new HttpLink({
            uri: getEnv("VITE_GRAPHQL_URL", "/graphql"),
            // Labels the actions an operator triggers here (assignments, gift cards) as ADMIN
            // instead of leaving them null, where the stats report cannot tell them apart from a
            // Mailchimp import — see services/graphql/context.ts.
            headers: { "X-Client-Platform": "ADMIN" },
        });

        const authLink = new ApolloLink((operation, forward) => {
            if (!isAuthenticated) return forward(operation);

            return new Observable((observer) => {
                getAccessTokenSilently()
                    .then((token) => {
                        releaseReauthAttempt();
                        operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
                            headers: { ...headers, Authorization: `Bearer ${token}` },
                        }));
                        forward(operation).subscribe(observer);
                    })
                    .catch((err) => {
                        // Once. A second failure after a full round trip through the identity
                        // provider is not a stale token, so the error goes to the UI — where
                        // `RequireAuth` renders it — instead of starting the trip again.
                        if (REAUTH_REQUIRED_ERRORS.has(err?.error) && claimReauthAttempt()) {
                            void loginWithRedirect({
                                appState: { returnTo: window.location.pathname },
                            });
                            return;
                        }
                        observer.error(err);
                    });
            });
        });

        const httpChain = authLink.concat(httpLink);

        // Route subscriptions to AppSync over WebSocket; everything else stays on
        // HTTP. When AppSync isn't configured (local dev), use the HTTP chain
        // alone — subscriptions simply won't connect.
        const appSyncLink = buildAppSyncSubscriptionLink(
            getEnv("VITE_APPSYNC_GRAPHQL_URL")
                ? { httpUrl: getEnv("VITE_APPSYNC_GRAPHQL_URL"), getToken: getAccessTokenSilently }
                : undefined,
        );

        const link = appSyncLink ? split(isSubscriptionOperation, appSyncLink, httpChain) : httpChain;

        return new ApolloClient({
            link,
            cache: createAdminCache(),
            defaultOptions: {
                watchQuery: { fetchPolicy: "cache-and-network" },
            },
        });
    }, [
        getAccessTokenSilently,
        loginWithRedirect,
        isAuthenticated,
    ]);

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
